/**
 * PricingContext - Global pricing and campaigns state management
 * Prevents multiple API calls by sharing pricing data across all components
 */

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from '../hooks/firebase/useAuth';
import { getAPIUrl } from '../config/api.config';

// Types
interface TierConfiguration {
  id: string;
  name: string;
  displayName: string;
  description?: string;
  price?: number;
  finalPrice?: number;
  basePrice?: number;
  currency?: string;
  interval?: string;
  features?: Array<{
    id: string;
    name: string;
    description?: string;
    included: boolean;
    limit?: string | number;
    premium?: boolean;
  }>;
  limits?: {
    dailySignals?: number;
    concurrentPositions?: number;
    instruments?: string[];
  };
  isPopular?: boolean;
  badge?: string;
}

interface CulturalPricingInfo {
  event: string;
  discount: number;
  startDate: string;
  endDate: string;
  description?: string;
}

interface PaymentMethodBonus {
  method: string;
  bonus: number;
}

interface ActivePromotion {
  id: string;
  name: string;
  discount: number;
  type: 'percentage' | 'fixed';
  tiers?: string[];
  userId?: string;
  startDate: string;
  endDate: string;
}

interface APIResponse<T> {
  success: boolean;
  data: T;
  error?: string;
}

interface PricingState {
  tiers: TierConfiguration[];
  culturalEvents: CulturalPricingInfo[];
  paymentBonuses: Record<string, number>;
  campaigns: ActivePromotion[];
  loading: boolean;
  error: string | null;
}

interface PricingActions {
  refreshPricingData: () => Promise<void>;
  getTierConfiguration: (tierName: string) => TierConfiguration | undefined;
  getActiveDiscounts: () => number;
  getBestPaymentBonus: () => number;
}

type PricingContextType = PricingState & PricingActions;

const PricingContext = createContext<PricingContextType | undefined>(undefined);

interface PricingProviderProps {
  children: React.ReactNode;
}

const API_BASE_URL = getAPIUrl();

export function PricingProvider({ children }: PricingProviderProps) {
  const { user } = useAuth();
  const [tiers, setTiers] = useState<TierConfiguration[]>([]);
  const [culturalEvents, setCulturalEvents] = useState<CulturalPricingInfo[]>([]);
  const [paymentBonuses, setPaymentBonuses] = useState<Record<string, number>>({});
  const [campaigns, setCampaigns] = useState<ActivePromotion[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Fetch all pricing data - SINGLE BATCH REQUEST
  const fetchPricingData = useCallback(async (userId?: string) => {
    try {
      setLoading(true);
      setError(null);

      // Batch all pricing API calls
      const [tiersResponse, culturalEventsResponse, paymentBonusesResponse, campaignsResponse] = await Promise.all([
        fetch(`${API_BASE_URL}/api/tiers/configuration`, {
          method: 'GET',
          headers: { 'Content-Type': 'application/json' }
        }),
        fetch(`${API_BASE_URL}/api/pricing/cultural-events`, {
          method: 'GET',
          headers: { 'Content-Type': 'application/json' }
        }),
        fetch(`${API_BASE_URL}/api/pricing/payment-methods/bonuses`, {
          method: 'GET',
          headers: { 'Content-Type': 'application/json' }
        }),
        fetch(`${API_BASE_URL}/api/pricing/campaigns/active${userId ? `?userId=${userId}` : ''}`, {
          method: 'GET',
          headers: { 'Content-Type': 'application/json' }
        })
      ]);

      // Process responses
      const tiersResult = tiersResponse.ok ? await tiersResponse.json() as APIResponse<TierConfiguration[]> : { success: false, data: [] };
      const culturalEventsResult = culturalEventsResponse.ok ? await culturalEventsResponse.json() as APIResponse<CulturalPricingInfo[]> : { success: false, data: [] };
      const paymentBonusesResult = paymentBonusesResponse.ok ? await paymentBonusesResponse.json() as APIResponse<PaymentMethodBonus[]> : { success: false, data: [] };
      const campaignsResult = campaignsResponse.ok ? await campaignsResponse.json() as APIResponse<ActivePromotion[]> : { success: false, data: [] };

      // Update state
      setTiers(tiersResult.success ? tiersResult.data : []);
      setCulturalEvents(culturalEventsResult.success ? culturalEventsResult.data : []);
      setCampaigns(campaignsResult.success ? campaignsResult.data : []);

      // Transform payment bonuses to Record<string, number>
      const bonusesRecord: Record<string, number> = {};
      if (paymentBonusesResult.success && Array.isArray(paymentBonusesResult.data)) {
        paymentBonusesResult.data.forEach((bonus: PaymentMethodBonus) => {
          bonusesRecord[bonus.method] = bonus.bonus;
        });
      }
      setPaymentBonuses(bonusesRecord);

    } catch (err) {
      console.error('Error fetching pricing data:', err);
      setError(err instanceof Error ? err.message : 'Failed to load pricing data');
      
      // Set defaults on error
      setTiers([]);
      setCulturalEvents([]);
      setPaymentBonuses({});
      setCampaigns([]);
    } finally {
      setLoading(false);
    }
  }, []);

  // Refresh pricing data (for manual refresh)
  const refreshPricingData = async () => {
    await fetchPricingData(user?.uid);
  };

  // Helper functions
  const getTierConfiguration = (tierName: string) => {
    return tiers.find(tier => tier.name === tierName || tier.displayName === tierName);
  };

  const getActiveDiscounts = () => {
    const now = new Date();
    let maxDiscount = 0;

    // Check cultural events
    culturalEvents.forEach(event => {
      const startDate = new Date(event.startDate);
      const endDate = new Date(event.endDate);
      if (now >= startDate && now <= endDate) {
        maxDiscount = Math.max(maxDiscount, event.discount);
      }
    });

    // Check campaigns
    campaigns.forEach(campaign => {
      const startDate = new Date(campaign.startDate);
      const endDate = new Date(campaign.endDate);
      if (now >= startDate && now <= endDate) {
        if (campaign.type === 'percentage') {
          maxDiscount = Math.max(maxDiscount, campaign.discount);
        }
      }
    });

    return maxDiscount;
  };

  const getBestPaymentBonus = () => {
    return Math.max(0, ...Object.values(paymentBonuses));
  };

  // Single useEffect - only triggers when user changes
  useEffect(() => {
    void fetchPricingData(user?.uid);
  }, [user?.uid, fetchPricingData]);

  const value: PricingContextType = {
    // State
    tiers,
    culturalEvents,
    paymentBonuses,
    campaigns,
    loading,
    error,
    
    // Actions
    refreshPricingData,
    getTierConfiguration,
    getActiveDiscounts,
    getBestPaymentBonus
  };

  return (
    <PricingContext.Provider value={value}>
      {children}
    </PricingContext.Provider>
  );
}

// Hook to use pricing context
export function usePricingContext() {
  const context = useContext(PricingContext);
  if (context === undefined) {
    throw new Error('usePricingContext must be used within a PricingProvider');
  }
  return context;
}

// Export types for use in other files
export type { 
  TierConfiguration, 
  CulturalPricingInfo, 
  PaymentMethodBonus, 
  ActivePromotion 
};